import { createWithEqualityFn } from 'zustand/traditional';
import { persistForUser } from '@erikdakoda/ui/shared/persistForUser';
import type { ArtifactType as ArtifactMimeType } from '@erikdakoda/artifact/shared/artifactTypes';

export type ArtifactType = ArtifactMimeType;

export type ArtifactEntry = {
  identifier: string;
  type: ArtifactType;
  title: string;
  content: string;
  display?: 'inline' | 'panel';
};

interface State {
  artifacts: Record<string, ArtifactEntry>;
  activeIdentifier: string | null;
  panelOpen: boolean;
  fullscreen: boolean;
  displayOverride: Record<string, 'inline' | 'panel'>;
  /** Bumped when artifact body metadata changes or refresh is requested (forces pointer reload). */
  contentRevision: Record<string, number>;
}

interface Action {
  upsertArtifact: (artifact: ArtifactEntry) => void;
  bumpContentRevision: (identifier: string) => void;
  openInPanel: (identifier: string) => void;
  openFullscreen: (identifier: string) => void;
  setFullscreen: (value: boolean) => void;
  setDisplayOverride: (identifier: string, display: 'inline' | 'panel') => void;
  closePanel: () => void;
}

function artifactEntryChanged(
  existing: ArtifactEntry | undefined,
  artifact: ArtifactEntry,
): boolean {
  if (!existing) {
    return true;
  }

  return (
    existing.content !== artifact.content ||
    existing.title !== artifact.title ||
    existing.type !== artifact.type ||
    existing.display !== artifact.display
  );
}

export const useArtifactStore = createWithEqualityFn<State & Action>()(
  persistForUser(
    (set) => ({
      artifacts: {},
      activeIdentifier: null,
      panelOpen: false,
      fullscreen: false,
      displayOverride: {},
      contentRevision: {},

      upsertArtifact: (artifact) =>
        set((state) => {
          const existing = state.artifacts[artifact.identifier];
          if (!artifactEntryChanged(existing, artifact)) {
            return state;
          }

          const nextRevision = (state.contentRevision[artifact.identifier] ?? 0) + 1;

          return {
            artifacts: { ...state.artifacts, [artifact.identifier]: artifact },
            contentRevision: {
              ...state.contentRevision,
              [artifact.identifier]: nextRevision,
            },
          };
        }),

      bumpContentRevision: (identifier) =>
        set((state) => ({
          contentRevision: {
            ...state.contentRevision,
            [identifier]: (state.contentRevision[identifier] ?? 0) + 1,
          },
        })),

      openInPanel: (identifier) =>
        set(() => ({ activeIdentifier: identifier, panelOpen: true, fullscreen: false })),

      openFullscreen: (identifier) =>
        set(() => ({ activeIdentifier: identifier, fullscreen: true })),

      setFullscreen: (value) => set(() => ({ fullscreen: value })),

      setDisplayOverride: (identifier, display) =>
        set((state) => ({
          displayOverride: { ...state.displayOverride, [identifier]: display },
        })),

      closePanel: () => set(() => ({ panelOpen: false, fullscreen: false })),
    }),
    {
      name: 'artifact-store',
      partialize: (state) => ({ displayOverride: state.displayOverride }),
    },
  ),
);
